import { Controller } from "@hotwired/stimulus"

import { frameFetch } from "@/lib/frame_fetch"

import type { Track } from "./embed_player_controller"

// What the frame may play (#2040, design D4). The card is one cached copy for
// every visitor, so who is listening and what they may play arrive here, in a
// private answer, after every load. The player and the card listen for
// `embed-access:changed`, and the card for `embed-access:failed`.
export interface AccessAnswer {
  session: "started" | "resumed"
  authentication: "anonymous" | "signed_in"
  csrf_token: string
  state:
    | "playable"
    | "authentication_required"
    | "join_required"
    | "purchase_required"
    | "unavailable"
  listener?: { name: string; email: string }
  tracks?: Track[]
  expires_at?: string
}

// Asking a minute early keeps a listener who taps the next track from meeting
// a token that ran out while they listened. A device whose clock runs ahead
// still waits that minute between answers instead of asking without pause.
const RENEW_EARLY_MS = 60_000
const RETRY_DELAYS_MS = [1_000, 3_000]

type Outcome = AccessAnswer | "transient" | "terminal"

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export default class extends Controller {
  static values = { url: String }

  declare urlValue: string

  // Answers can arrive out of order; only the newest request may speak, and
  // none once the frame has gone.
  #latest = 0
  #renewal: ReturnType<typeof setTimeout> | undefined

  connect() {
    void this.refresh()
  }

  disconnect() {
    clearTimeout(this.#renewal)
    this.#latest++
  }

  // data-action="pageshow@window->embed-access#restored"
  // A page the browser restores from its history still shows whoever listened
  // before. A sign-out or a revoked grant must not come back with it.
  restored(event: PageTransitionEvent) {
    if (event.persisted) void this.refresh()
  }

  // The card asks again after a sign-in, a join or a sign-out. A blip in the
  // network or the database is retried twice before the card is told, so a
  // listener sees "try again" only when trying again is what is left.
  async refresh() {
    const request = ++this.#latest
    let outcome: Outcome = "transient"
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) await wait(RETRY_DELAYS_MS[attempt - 1])
      if (request !== this.#latest) return
      outcome = await this.fetchAnswer()
      if (outcome !== "transient") break
    }
    if (request !== this.#latest) return

    if (typeof outcome === "string") {
      this.dispatch("failed")
      return
    }
    this.scheduleRenewal(outcome)
    this.dispatch("changed", { detail: outcome })
  }

  private async fetchAnswer(): Promise<Outcome> {
    let response: Response
    try {
      response = await frameFetch(this.urlValue, { cache: "no-store" })
    } catch {
      return "transient"
    }
    if (!response.ok) {
      return response.status >= 500 || response.status === 429
        ? "transient"
        : "terminal"
    }
    try {
      return (await response.json()) as AccessAnswer
    } catch {
      return "terminal"
    }
  }

  private scheduleRenewal(answer: AccessAnswer) {
    clearTimeout(this.#renewal)
    if (!answer.expires_at) return

    const wait = Date.parse(answer.expires_at) - Date.now() - RENEW_EARLY_MS
    this.#renewal = setTimeout(
      () => void this.refresh(),
      Math.max(wait, RENEW_EARLY_MS),
    )
  }
}
